import { useState } from 'react';

import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { getApiErrorMessage } from 'utils/api-error';
import {
  downloadPackage,
  IExportRequest,
  isRefusal,
  migrationRoutes,
  readBlobError,
  readExportFailure
} from 'utils/PatientMigration';

interface IExportPatientsModalProps {
  uids: string[];
  onClose: (exported?: boolean) => void;
}

const ExportPatientsModal = ({ uids, onClose }: IExportPatientsModalProps) => {
  const { formatMessage } = useIntl();
  const [intent, setIntent] = useState<IMigrationIntent>('backup');
  const [destination, setDestination] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [unmovable, setUnmovable] = useState<IMigrationExportFailure | null>(null);
  const [failed, setFailed] = useState(false);

  const [{ loading }, post] = useAxios<Blob>(
    { method: 'POST', url: migrationRoutes.exportPatients, responseType: 'blob' },
    { manual: true }
  );

  // A move needs somewhere to go; a backup must not carry one. The request type enforces this,
  // so the form has to match it rather than send an empty string.
  const destinationMissing = intent === 'move' && !destination.trim();

  const handleExport = async () => {
    if (destinationMissing) return;
    const data: IExportRequest =
      intent === 'move'
        ? { uids, intent, destinationEnvironment: destination.trim() }
        : { uids, intent };
    setRefusal(null);
    setUnmovable(null);
    setFailed(false);
    try {
      const response = await post({ data });
      if (response?.data) {
        // The server names the package after its id; match it so a saved file can be traced back.
        const packageId = response.headers?.['x-migration-package-id'];
        downloadPackage(
          response.data,
          packageId ? `caresimple-migration-${packageId}.zip` : `patient-package-${Date.now()}.zip`
        );
        onClose(true);
      }
    } catch (caught) {
      // Refused before anything was marked. This is the last point in the move where nothing has
      // happened yet, so the list of devices is the whole instruction: unassign these and export
      // again, or take a backup instead.
      const refused = await readExportFailure(caught);
      if (refused) {
        setUnmovable(refused);
        return;
      }
      // The body is a zip on success, so a refusal's JSON arrives as a Blob and has to be read
      // back before it says anything. Without this the server's reason is replaced by "try again".
      const read = await readBlobError(caught);
      if (isRefusal(read)) {
        setRefusal(getApiErrorMessage(read, formatMessage({ id: 'migrationExportFailedKey' })));
        return;
      }
      setFailed(true);
    }
  };

  return (
    <Modal show backdrop="static" data-test="export-patients-modal">
      <Modal.Header closeButton onHide={() => onClose()}>
        <Modal.Title>
          {formatMessage({ id: 'migrationExportTitleKey' }, { count: uids.length })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <Loading />}
        {failed && <Error retry={handleExport} />}
        {refusal && (
          <Alert variant="danger" data-test="export-patients-refused">
            {refusal}
          </Alert>
        )}
        {unmovable && (
          <Alert variant="danger" data-test="export-patients-unmovable">
            <div className="mb-1">{unmovable.message}</div>
            {(unmovable.unmovable?.length ?? 0) > 0 && (
              <ul className="mb-0">
                {unmovable.unmovable?.map((device, index) => (
                  <li key={device.deviceId ?? device.imei ?? `unmovable-${index}`}>
                    <span className="font-monospace">{device.imei ?? device.deviceId}</span>
                    {device.reason ? `: ${device.reason}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <Form.Group className="form-group mb-3">
          <Form.Check
            type="radio"
            id="export-intent-backup"
            data-test="export-intent-backup"
            label={formatMessage({ id: 'migrationIntentBackupKey' })}
            checked={intent === 'backup'}
            onChange={() => setIntent('backup')}
          />
          <small className="text-gray-550 ms-4">
            {formatMessage({ id: 'migrationIntentBackupHintKey' })}
          </small>
          <Form.Check
            type="radio"
            id="export-intent-move"
            data-test="export-intent-move"
            className="mt-2"
            label={formatMessage({ id: 'migrationIntentMoveKey' })}
            checked={intent === 'move'}
            onChange={() => setIntent('move')}
          />
          <small className="text-gray-550 ms-4">
            {formatMessage({ id: 'migrationIntentMoveHintKey' })}
          </small>
        </Form.Group>

        {intent === 'move' && (
          <Form.Group className="form-group">
            <Form.Label>{formatMessage({ id: 'migrationDestinationKey' })}</Form.Label>
            <Form.Control
              value={destination}
              data-test="export-destination"
              onChange={(event) => setDestination(event.target.value)}
            />
            <Alert variant="warning" className="mt-2 mb-0">
              {formatMessage({ id: 'migrationMoveWarningKey' })}
            </Alert>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={() => onClose()} data-test="export-patients-cancel">
          {formatMessage({ id: 'Cancel' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleExport}
          disabled={loading || destinationMissing}
          data-test="export-patients-confirm"
        >
          {formatMessage({ id: 'migrationExportConfirmKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportPatientsModal;
